import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { useSelector } from 'react-redux';

import { useAuth } from '../../providers/AuthProvider';
import { selectUserInfos } from '../../store/userDataStore';
import {
  moodleSyncService, MoodleSyncAction, MoodleSyncCycle, MoodleSyncStatus,
} from '../../services/moodleSyncService';

const POLL_INTERVAL_MS = 10_000;

const formatDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('fr-FR') : '—');

const OUTCOME_LABELS: Record<MoodleSyncCycle['outcome'], string> = {
  ok: 'Terminé',
  no_token: "Token d'API absent",
  session_expired: 'Session Moodle expirée',
  error: 'Erreur (voir les journaux)',
};

// Clés d'alerte du worker -> libellés lisibles (import_<item>, empty_<item> portent le nom de l'item).
const alertLabel = (key: string): string => {
  if (key === 'session') return 'Session Moodle expirée';
  if (key === 'api_token') return "Token d'API refusé par pwa-dashboard";
  if (key.startsWith('import_')) return `Import Moodle en échec : ${key.slice('import_'.length)}`;
  if (key.startsWith('empty_')) return `CSV vide, import ignoré : ${key.slice('empty_'.length)}`;
  return key;
};

// Pilote le worker moodle-sync (conteneur interne) : pause, reprise, cycle immédiat. Toutes les actions
// passent par l'API, qui relaie au worker ; la page se rafraîchit seule toutes les 10 s.
const AdminMoodleSyncPage: React.FC = () => {
  const { getValidToken } = useAuth();
  const userInfos = useSelector(selectUserInfos);
  const isAdmin = userInfos?.access === 'admin';

  const [status, setStatus] = useState<MoodleSyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<MoodleSyncAction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      setStatus(await moodleSyncService.getStatus(token));
      setError(null);
    } catch (e: any) {
      setStatus(null);
      setError(e.message || "Impossible de lire l'état de la synchronisation");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    load();
    const timer = window.setInterval(load, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [isAdmin, load]);

  // userInfos est rechargé de façon asynchrone après un F5 : on attend avant de juger l'accès
  if (!userInfos) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Accès réservé aux administrateurs.</Alert>
      </Box>
    );
  }

  const handleAction = async (action: MoodleSyncAction) => {
    setActing(action);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      setStatus(await moodleSyncService.act(action, token));
      setError(null);
    } catch (e: any) {
      setError(e.message || "Erreur lors de l'action");
    } finally {
      setActing(null);
    }
  };

  const state = status && (status.running
    ? { label: 'Cycle en cours', color: 'info' as const }
    : status.paused
      ? { label: 'En pause', color: 'warning' as const }
      : { label: 'Actif', color: 'success' as const });
  const session = status && {
    ok: { label: 'Valide', color: 'success' as const },
    expired: { label: 'Expirée', color: 'error' as const },
    unknown: { label: 'Pas encore vérifiée', color: 'default' as const },
  }[status.session];
  const api = status && {
    ok: { label: 'Accepté', color: 'success' as const },
    token_refused: { label: 'Refusé', color: 'error' as const },
    unknown: { label: 'Pas encore vérifié', color: 'default' as const },
  }[status.api];
  const alerts = status ? Object.entries(status.alerts) : [];

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h4">Synchronisation Moodle</Typography>
      <Typography variant="body2" color="text.secondary">
        Pousse dans le cours Moodle les notes « Auto-inscription », « Composition groupe », « PREPA.TS1 » et « PREPA.TS2 » : toutes les{' '}
        {status?.interval_min ?? 10} minutes, seuls les fichiers modifiés sont importés.
      </Typography>

      {error && <Alert severity="warning">{error}</Alert>}

      {loading && <CircularProgress size={28} />}

      {status && state && session && api && (
        <>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Synchronisation</Typography>
                <Chip label={state.label} color={state.color} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Session Moodle</Typography>
                <Chip label={session.label} color={session.color} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Token d'API</Typography>
                <Chip label={api.label} color={api.color} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Prochain cycle</Typography>
                <Typography>{status.paused ? '—' : formatDate(status.next_cycle_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Dernier cycle</Typography>
                <Typography>
                  {status.last_cycle
                    ? `${formatDate(status.last_cycle.finished_at)} — ${OUTCOME_LABELS[status.last_cycle.outcome]}`
                    : '—'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {status.paused ? (
                <Button variant="contained" onClick={() => handleAction('resume')} disabled={acting !== null}>
                  {acting === 'resume' ? <CircularProgress size={20} /> : 'Reprendre'}
                </Button>
              ) : (
                <Button variant="outlined" color="warning" onClick={() => handleAction('pause')} disabled={acting !== null}>
                  {acting === 'pause' ? <CircularProgress size={20} /> : 'Mettre en pause'}
                </Button>
              )}
              <Button variant="outlined" onClick={() => handleAction('run-now')}
                disabled={acting !== null || status.running}>
                {acting === 'run-now' ? <CircularProgress size={20} /> : 'Synchroniser maintenant'}
              </Button>
            </Box>
          </Paper>

          {alerts.length > 0 && (
            <Alert severity="error">
              <Typography variant="subtitle2">Problèmes en cours</Typography>
              {alerts.map(([key, since]) => (
                <Typography key={key} variant="body2">{alertLabel(key)} (depuis le {formatDate(since)})</Typography>
              ))}
            </Alert>
          )}

          {status.session === 'expired' && (
            <Alert severity="info">
              Pour renouveler la session : depuis votre poste, lancez{' '}
              <code>python3 script_python/renew_moodle_session.py --run-cycle</code>. La connexion CAS et le code
              MFA se font dans une fenêtre de navigateur ; les imports reprennent ensuite seuls.
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Note Moodle</TableCell>
                  <TableCell>Dernier import</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(status.imports).map(([name, item]) => (
                  <TableRow key={name}>
                    <TableCell>{item.label}</TableCell>
                    <TableCell>{formatDate(item.last_import_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default AdminMoodleSyncPage;
